import { defineMessages } from "./define";

/** The user directory, access lists, and signing in. */
export const users = defineMessages({
  ja: {
    "users.title": "ユーザー管理",
    "users.toTeams": "チーム管理へ",
    "users.heading": "ユーザー",
    "users.count.one": "{count} 人",
    "users.count.other": "{count} 人",
    "users.countFiltered": "{shown} / {total} 人",
    "users.empty":
      "まだ誰もログインしていません。ユーザーは初回ログイン時に自動で登録されます。",
    "users.filterPlaceholder": "名前・メールアドレスで絞り込み",
    "users.filterLabel": "ユーザーを絞り込む",
    "users.noMatch": "一致するユーザーはいません。",
    "users.listLabel": "ユーザー",
    "users.selectPrompt": "ユーザーを選ぶと、所属チームが表示されます。",
    "users.allowlistNote":
      "許可リストで管理者に指定されています。管理者チームから外しても管理者のままです。",
    "users.email": "メールアドレス",
    "users.firstSignIn": "初回ログイン",
    "users.lastSignIn": "最終ログイン",
    "users.teamToAdd": "追加するチーム",
    "users.noJoinableTeams": "追加できるチームがありません",
    "users.chooseTeam": "チームを選択",
    "users.addToTeam": "チームに追加",
    "users.loading": "読み込み中…",
    "users.noTeams": "どのチームにも所属していません。",
    "users.leaveTitle": "{team} から外す",
    "users.leaveDescription": "{email} を {team} のメンバーから外します。",

    "permissions.title": "アクセス権限",
    "permissions.level.METADATA": "存在とメタデータのみ",
    "permissions.level.VIEW": "閲覧（PDF を開ける）",
    "permissions.level.EDIT": "閲覧と更新",
    "permissions.level.UPLOAD": "アップロードのみ",
    "permissions.hint.METADATA":
      "タイトルや相手方は見えますが PDF は開けません。存在を知らせて権限申請の起点にするための権限です。",
    "permissions.hint.VIEW":
      "PDF を開けます。フォルダに付けると中の契約書にも及びます。",
    "permissions.hint.EDIT": "名前の変更・移動・削除と、権限の設定ができます。",
    "permissions.hint.UPLOAD":
      "このフォルダにアップロードできますが、中にある他の契約書は見えません。",
    "permissions.alwaysIn.FOLDER": "管理者チームと、このフォルダを作成した人",
    "permissions.alwaysIn.CONTRACT":
      "管理者チームと、この契約書をアップロードした人",
    "permissions.none":
      "権限が設定されていません。いまアクセスできるのは{alwaysIn}だけです。",
    "permissions.some":
      "下に並んだ相手に加えて、{alwaysIn}がアクセスできます。",
    "permissions.needEdit":
      "このリソースの権限を変更するには、更新権限が必要です。",
    "permissions.principalType": "対象の種類",
    "permissions.principalType.TEAM": "チーム",
    "permissions.principalType.USER": "ユーザー",
    "permissions.principal": "対象",
    "permissions.choose": "選択してください",
    "permissions.level": "権限",
    "permissions.userWithEmail": "{name}（{email}）",
    "permissions.teamName": "{name}（チーム）",

    "auth.prompt": "SORACOM の Google アカウントでサインインしてください。",
    "auth.redirecting": "リダイレクト中…",
    "auth.signIn": "Google でサインイン",
    "auth.purpose": "社内の法務業務用です。",
    "auth.signingIn": "サインインしています…",
    "auth.error.MISSING_CODE": "認可コードがありません",
    "auth.error.PENDING_NOT_FOUND":
      "サインインの途中経過が見つかりません。最初からやり直してください。",
    "auth.error.STATE_MISMATCH":
      "state が一致しません。サインインを中断しました。",
    "auth.error.TOKEN_REQUEST_FAILED":
      "トークンの取得に失敗しました ({status})",
  },
  en: {
    "users.title": "Users",
    "users.toTeams": "Go to teams",
    "users.heading": "Users",
    "users.count.one": "{count} person",
    "users.count.other": "{count} people",
    "users.countFiltered": "{shown} of {total} people",
    "users.empty":
      "Nobody has signed in yet. People are added automatically the first time they sign in.",
    "users.filterPlaceholder": "Filter by name or email",
    "users.filterLabel": "Filter users",
    "users.noMatch": "No users match.",
    "users.listLabel": "Users",
    "users.selectPrompt": "Select a user to see their teams.",
    "users.allowlistNote":
      "Made an administrator by the allowlist. They stay an administrator even if removed from the administrators team.",
    "users.email": "Email",
    "users.firstSignIn": "First sign-in",
    "users.lastSignIn": "Last sign-in",
    "users.teamToAdd": "Team to add",
    "users.noJoinableTeams": "No teams to add",
    "users.chooseTeam": "Select a team",
    "users.addToTeam": "Add to team",
    "users.loading": "Loading…",
    "users.noTeams": "Not on any team.",
    "users.leaveTitle": "Remove from {team}",
    "users.leaveDescription": "{email} will be removed from {team}.",

    "permissions.title": "Access",
    "permissions.level.METADATA": "Existence and metadata only",
    "permissions.level.VIEW": "View (can open the PDF)",
    "permissions.level.EDIT": "View and edit",
    "permissions.level.UPLOAD": "Upload only",
    "permissions.hint.METADATA":
      "Can see the title and counterparty but cannot open the PDF. Use it to let someone know a contract exists so they can request access.",
    "permissions.hint.VIEW":
      "Can open the PDF. On a folder, it also covers the contracts inside.",
    "permissions.hint.EDIT": "Can rename, move, and delete, and manage access.",
    "permissions.hint.UPLOAD":
      "Can upload to this folder but cannot see the other contracts in it.",
    "permissions.alwaysIn.FOLDER":
      "the administrators team and the person who created this folder",
    "permissions.alwaysIn.CONTRACT":
      "the administrators team and the person who uploaded this contract",
    "permissions.none":
      "No access is set. Right now only {alwaysIn} can open it.",
    "permissions.some": "Besides those listed below, {alwaysIn} can open it.",
    "permissions.needEdit": "You need edit access to change who can open this.",
    "permissions.principalType": "Grant to",
    "permissions.principalType.TEAM": "Team",
    "permissions.principalType.USER": "User",
    "permissions.principal": "Who",
    "permissions.choose": "Select…",
    "permissions.level": "Access level",
    "permissions.userWithEmail": "{name} ({email})",
    "permissions.teamName": "{name} (team)",

    "auth.prompt": "Sign in with your SORACOM Google account.",
    "auth.redirecting": "Redirecting…",
    "auth.signIn": "Sign in with Google",
    "auth.purpose": "For internal legal work.",
    "auth.signingIn": "Signing in…",
    "auth.error.MISSING_CODE":
      "The sign-in response has no authorization code.",
    "auth.error.PENDING_NOT_FOUND":
      "No sign-in in progress was found. Please start again.",
    "auth.error.STATE_MISMATCH":
      "The sign-in state did not match, so the sign-in was stopped.",
    "auth.error.TOKEN_REQUEST_FAILED": "Could not get a token ({status}).",
  },
});
